import type { ReactNode } from "react";

/** One rubric component the AI scored the resume on (mirrors the API's ResumeComponent). */
export interface ScanComponent {
  name: string;
  score: number | null;
  note?: string | null;
}

/** Rubric v2 (F3): the structured basis of the score, when this scan was scored under it. */
export interface ScanRubric {
  rubricVersion: number;
  components: { id: string; score: number; evidence: string; gaps: string[] }[];
  facts: {
    evidencedYears: number;
    minYears: number | null;
    gaps: { fromYm: string; toYm: string; months: number }[];
    flags: string[];
  };
  missing: string[];
  profile: { role?: string; minYears?: number | null; mustHaves?: string[]; domain?: string };
}

/** A scored resume as returned by POST/GET api/requisitions/{id}/resumes. */
export interface Scan {
  id: string;
  filename: string;
  candidateName: string | null;
  candidateEmail: string | null;
  overall: number | null;
  components: ScanComponent[];
  summary: string | null;
  selected: boolean;
  candidacyId: string | null;
  createdAt?: string;
  invitedAt?: string | null;
  rubric?: ScanRubric;
  /** What the scan read off the resume; null/absent on scans from before it existed. */
  profile?: import("./ResumeProfileView.js").ResumeProfileData | null;
}

/**
 * What a number MEANS, in the same three bands the colour already uses (QA #38: "is 50 good?").
 * Deliberately derived from the same thresholds as scoreColor — a label that could drift from the
 * colour beside it would be worse than no label.
 *
 * These describe fit against THIS requirement, not the candidate. Wording avoids "recommended":
 * we never recommend, a person decides.
 */
function scoreBand(n: number | null): string | null {
  if (n == null) return null;
  if (n >= 75) return "strong match";
  if (n >= 50) return "moderate match";
  return "weak match";
}

/** Green ≥75, amber ≥50, red below, muted when the AI could not score the file. */
function scoreColor(n: number | null): string {
  if (n == null) return "var(--ink-mute)";
  if (n >= 75) return "var(--good)";
  if (n >= 50) return "var(--warn)";
  return "var(--crit)";
}

/** Overall desc, unscored (null) last, then stable by filename — best fit reads top-down. */
function bestFirst(a: Scan, b: Scan): number {
  const av = a.overall ?? -1;
  const bv = b.overall ?? -1;
  if (av !== bv) return bv - av;
  return a.filename.localeCompare(b.filename);
}

interface Props {
  scans: Scan[];
  /** ids the recruiter has checkbox-picked to interview. */
  selected: Set<string>;
  onToggle: (id: string) => void;
  /** while true the checkboxes are frozen (a select/submit is in flight). */
  disabled?: boolean;
}

/**
 * The scored-resume list for New Request — every uploaded resume with its AI overall score,
 * per-competency breakdown and summary, best fit first. Each row is a selectable card: ticking it
 * adds the candidate to the interview selection that drives the live cost panel.
 */
export function ResumeScoreList({ scans, selected, onToggle, disabled }: Props): ReactNode {
  const sorted = [...scans].sort(bestFirst);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* QA #39: the bars carried colour with nothing to read it by. One legend for the list,
          not a repeat per row. */}
      {sorted.length > 0 && (
        <div className="mut sm" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span>Every resume is scored on the same five weighted components; the overall is their weighted average:</span>
          {(
            [
              ["strong match", 80, "meets the must-haves with quoted evidence"],
              ["moderate match", 60, "meets most; gaps listed for the interview"],
              ["weak match", 20, "material gaps against the must-haves"],
            ] as const
          ).map(([label, v, def]) => (
            <span key={label} title={def} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <i style={{ width: 22, height: 6, borderRadius: 3, background: scoreColor(v), display: "inline-block" }} />
              {label}
            </span>
          ))}
        </div>
      )}
      {sorted.map((scan) => {
        const isOn = selected.has(scan.id);
        const already = scan.candidacyId != null;
        const name = scan.candidateName?.trim() || scan.filename;
        const col = scoreColor(scan.overall);
        return (
          <div
            key={scan.id}
            className={`rq-scan${isOn ? " on" : ""}${already ? " done" : ""}`}
            onClick={disabled || already ? undefined : () => onToggle(scan.id)}
          >
            <span className="box">{isOn ? "✓" : ""}</span>

            <div className="body">
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span className="nm">{name}</span>
                {already && (
                  <span className="pill v" style={{ fontSize: 12 }}>
                    Invited
                  </span>
                )}
              </div>
              <div className="sub">{scan.candidateEmail || scan.filename}</div>

              {scan.summary && <div className="sm-summary">{scan.summary}</div>}

              {scan.components.length > 0 && (
                <div className="comps">
                  {scan.components.map((c, i) => (
                    <div key={`${scan.id}-${i}`} className="comp">
                      <span className="cn" title={c.note ?? c.name}>
                        {c.name}
                      </span>
                      <span className="track">
                        <i
                          style={{
                            width: `${Math.max(0, Math.min(100, c.score ?? 0))}%`,
                            background: scoreColor(c.score),
                          }}
                        />
                      </span>
                      <span className="cv tnum" style={{ color: scoreColor(c.score) }}>
                        {c.score ?? "—"}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* RUBRIC v2 — the justifiable score (F3). The flags are computed facts (years
                  arithmetic, timeline gaps), stated neutrally; the missing list is the union of
                  what the requirement asks for that the resume never shows. This block IS the
                  answer to "why 80?" — every number above can be reproduced from it. */}
              {scan.rubric && (
                <div className="sm" style={{ marginTop: 6, display: "grid", gap: 4 }}>
                  {scan.rubric.facts.minYears != null && (
                    <div className="mut">
                      Experience: ~{scan.rubric.facts.evidencedYears} yrs relevant evidenced ·
                      requirement asks {scan.rubric.facts.minYears} yrs
                    </div>
                  )}
                  {scan.rubric.facts.flags.map((f, i) => (
                    <div key={`fl-${i}`} className="mut" style={{ color: "var(--warn)" }}>
                      ⚑ {f}
                    </div>
                  ))}
                  {scan.rubric.missing.length > 0 && (
                    <div className="mut">
                      <b>Not evidenced:</b> {scan.rubric.missing.slice(0, 4).join(" · ")}
                      {scan.rubric.missing.length > 4 && ` · +${scan.rubric.missing.length - 4} more`}
                    </div>
                  )}
                  {(() => {
                    const best = [...scan.rubric.components].sort((a, b) => b.score - a.score)[0];
                    return best && best.evidence ? (
                      <div className="mut" style={{ fontStyle: "italic" }}>
                        “{best.evidence.slice(0, 180)}{best.evidence.length > 180 ? "…" : ""}”
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>

            <div className="score">
              <div className="v tnum" style={{ color: col }}>
                {scan.overall ?? "—"}
              </div>
              <div className="o">{scan.overall == null ? "no score" : "/ 100"}</div>
              {scoreBand(scan.overall) && (
                <div className="o" style={{ color: col, fontWeight: 650 }}>{scoreBand(scan.overall)}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
